// Hooks de datos del catálogo de reportes del Admin SK. Las URLs viven en
// `api/services/datalake-query/`; sin base URL ninguna query se dispara.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { analyticsApi, isAnalyticsConfigured } from '@/api/services/datalake-query';
import type { ReportRequest } from '@/api/services/datalake-query';
import { shouldRetryReportsQuery } from '../logic';

const KEY = ['credit', 'reports'] as const;

/** `true` si el ambiente declara la base URL del motor de reportes. */
export function useReportsServiceConfigured(): boolean {
    return isAnalyticsConfigured();
}

/** Las tablas que este tenant puede consultar; su respuesta decide si el servicio está disponible. */
export function useLakeCatalog(configured: boolean) {
    return useQuery({
        queryKey: [...KEY, 'catalog'],
        queryFn: () => analyticsApi.listCatalog(),
        enabled: configured,
        retry: shouldRetryReportsQuery,
        staleTime: 60_000,
    });
}

/** El historial de corridas; solo se pide cuando el catálogo ya respondió. */
export function useReportJobs(enabled: boolean) {
    return useQuery({
        queryKey: [...KEY, 'jobs'],
        queryFn: () => analyticsApi.listJobs(),
        enabled,
        retry: shouldRetryReportsQuery,
        staleTime: 30_000,
    });
}

/** Las columnas de una tabla del lake, para describir el archivo sin filas de ejemplo. */
export function useTableColumns(qualifiedName: string | null) {
    return useQuery({
        queryKey: [...KEY, 'columns', qualifiedName],
        queryFn: () => analyticsApi.describeTable(qualifiedName ?? ''),
        enabled: Boolean(qualifiedName) && isAnalyticsConfigured(),
        retry: shouldRetryReportsQuery,
        staleTime: 5 * 60_000,
    });
}

/** Arranca una corrida y refresca el historial, donde queda registrada. */
export function useRunReport() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request: ReportRequest) => analyticsApi.runReport(request),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [...KEY, 'jobs'] });
        },
    });
}

/** Baja el CSV de un job terminado. */
export function useDownloadJobCsv() {
    return useMutation({
        mutationFn: (jobId: string) => analyticsApi.getJobCsv(jobId),
    });
}
